import axios from "axios";

const BASE = (process.env.REACT_APP_API_ENDPOINT || "").replace(/\/$/, "");
const API_KEY = process.env.REACT_APP_API_KEY;

// Sessions ------------------------------------------------------------------
// Parents and alumni have separate sessions so one login can never be used as
// the other. Stored as { access, refresh, user }.

const KEY = (role) => `tis.session.${role}`;
const listeners = new Set();

function read(role) {
  try {
    return JSON.parse(localStorage.getItem(KEY(role))) || null;
  } catch {
    return null;
  }
}

export const session = {
  get: read,
  set(role, data) {
    try {
      localStorage.setItem(KEY(role), JSON.stringify(data));
    } catch {
      /* storage unavailable */
    }
    listeners.forEach((fn) => fn(role));
  },
  clear(role) {
    try {
      localStorage.removeItem(KEY(role));
    } catch {
      /* storage unavailable */
    }
    listeners.forEach((fn) => fn(role));
  },
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

// The old site stored one shared token under these keys; drop them.
try {
  localStorage.removeItem("userToken");
  localStorage.removeItem("userID");
} catch {
  /* ignore */
}

// Clients -------------------------------------------------------------------

function makeClient(role) {
  const client = axios.create({ baseURL: `${BASE}/api/`, timeout: 120000 });
  client.interceptors.request.use((config) => {
    if (API_KEY) config.headers["X-API-KEY"] = API_KEY;
    const s = role ? read(role) : null;
    if (s?.access) config.headers.Authorization = `Bearer ${s.access}`;
    return config;
  });
  if (role) {
    client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config || {};
        if (error.response?.status === 401 && !original._retry) {
          const s = read(role);
          if (s?.refresh) {
            original._retry = true;
            try {
              const { data } = await axios.post(
                `${BASE}/api/admission/token/refresh/`,
                { refresh: s.refresh },
                { headers: API_KEY ? { "X-API-KEY": API_KEY } : {} }
              );
              session.set(role, { ...s, access: data.access });
              original.headers = { ...(original.headers || {}), Authorization: `Bearer ${data.access}` };
              return client(original);
            } catch {
              /* fall through to sign-out */
            }
          }
          session.clear(role);
        }
        return Promise.reject(error);
      }
    );
  }
  return client;
}

const publicApi = makeClient(null);
const parentApi = makeClient("parent");
const alumniApi = makeClient("alumni");

export function errorMessage(error, fallback = "Something went wrong. Please try again.") {
  const data = error?.response?.data;
  if (data) {
    if (typeof data.error === "string") return data.error;
    if (typeof data.detail === "string") return data.detail;
    if (typeof data === "object") {
      for (const value of Object.values(data)) {
        if (Array.isArray(value) && typeof value[0] === "string") return value[0];
        if (typeof value === "string") return value;
      }
    }
  }
  if (error?.code === "ECONNABORTED") return "The request took too long. Please check your internet and try again.";
  if (error?.message === "Network Error") return "Cannot reach the server. Please check your internet connection.";
  return fallback;
}

export const mediaUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  return `${BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

// Admissions: public ---------------------------------------------------------

let configPromise = null;
export function getAdmissionConfig() {
  if (!configPromise) {
    configPromise = publicApi
      .get("admission/config/")
      .then((r) => r.data)
      .catch((e) => {
        configPromise = null;
        throw e;
      });
  }
  return configPromise;
}

export async function parentSignup(payload) {
  const { data } = await publicApi.post("admission/signup/", payload);
  session.set("parent", { ...data.token, user: data.user });
  return data;
}

export async function parentLogin(email, password) {
  const { data } = await publicApi.post("admission/login/", { email, password });
  session.set("parent", { ...data.token, user: data.user });
  return data;
}

export const parentLogout = () => session.clear("parent");

export const parentForgotPassword = (email) =>
  publicApi.post("admission/password/forgot/", { email }).then((r) => r.data);

export const parentResetPassword = (token, newPassword, confirmPassword) =>
  publicApi
    .post("admission/password/reset/", { token, new_password: newPassword, confirm_password: confirmPassword })
    .then((r) => r.data);

// Admissions: signed-in parent ------------------------------------------------

export const getParentProfile = () => parentApi.get("admission/me/").then((r) => r.data);

export async function updateParentProfile(payload) {
  const { data } = await parentApi.patch("admission/me/", payload);
  const s = read("parent");
  if (s) session.set("parent", { ...s, user: data });
  return data;
}

export async function changeParentPassword(currentPassword, newPassword) {
  const { data } = await parentApi.post("admission/me/password/", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  const s = read("parent");
  if (s && data.token) session.set("parent", { ...s, ...data.token });
  return data;
}

export const listChildren = () => parentApi.get("admission/children/").then((r) => r.data);

export const getChild = (id) => parentApi.get(`admission/children/${id}/`).then((r) => r.data);

export function submitApplication(payload, { photo, signature } = {}) {
  const form = new FormData();
  form.append("data", JSON.stringify(payload));
  if (photo) form.append("photo", photo);
  if (signature) form.append("signature", signature, signature.name || "signature.png");
  return parentApi.post("admission/children/", form).then((r) => r.data);
}

export function uploadChildDocument(childId, docType, file) {
  const form = new FormData();
  form.append("file", file);
  return parentApi.post(`admission/children/${childId}/documents/${docType}/`, form).then((r) => r.data);
}

export function uploadSchoolRecord(childId, file) {
  const form = new FormData();
  form.append("file", file);
  return parentApi.post(`admission/children/${childId}/school-records/`, form).then((r) => r.data);
}

export const deleteSchoolRecord = (childId, recordId) =>
  parentApi.delete(`admission/children/${childId}/school-records/${recordId}/`).then((r) => r.data);

/** Submit one of the portal forms. `data` may be a plain object (JSON) or FormData (files). */
export const submitChildForm = (childId, formKey, data) =>
  parentApi.post(`admission/children/${childId}/forms/${formKey}/`, data).then((r) => r.data);

// Alumni ------------------------------------------------------------------------

export function alumniSignup(fields, image) {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, typeof v === "object" ? JSON.stringify(v) : v);
  });
  if (image) form.append("image", image);
  return publicApi.post("tismain/alumsignup/", form).then((r) => r.data);
}

export async function alumniLogin(email, password) {
  const { data } = await publicApi.post("tismain/alumlogin/", { email, password });
  session.set("alumni", { ...data.token, user: data.user });
  return data;
}

export const alumniLogout = () => session.clear("alumni");

export const alumniForgotPassword = (email) =>
  publicApi.post("tismain/alumni/password/forgot/", { email }).then((r) => r.data);

export const alumniResetPassword = (token, newPassword, confirmPassword) =>
  publicApi
    .post("tismain/alumni/password/reset/", { token, new_password: newPassword, confirm_password: confirmPassword })
    .then((r) => r.data);

export const getAlumniProfile = () => alumniApi.get("tismain/alumni/me/").then((r) => r.data);

export async function updateAlumniProfile(fields, image) {
  let body = fields;
  if (image) {
    body = new FormData();
    Object.entries(fields).forEach(([k, v]) => v !== undefined && v !== null && body.append(k, v));
    body.append("image", image);
  }
  const { data } = await alumniApi.patch("tismain/alumni/me/", body);
  const s = read("alumni");
  if (s) session.set("alumni", { ...s, user: data });
  return data;
}

export const addWorkHistory = (item) => alumniApi.post("tismain/alumni/me/work/", item).then((r) => r.data);
export const deleteWorkHistory = (id) => alumniApi.delete(`tismain/alumni/me/work/${id}/`);

export const alumniDirectory = (params) =>
  alumniApi.get("tismain/alumni/directory/", { params }).then((r) => r.data);

// Website content ------------------------------------------------------------------

export const fetchEvents = () => publicApi.get("tismain/events/").then((r) => r.data);
export const fetchFeaturedAlumni = () => publicApi.get("tismain/displayed-alumni/").then((r) => r.data);
export const fetchAlumniEvents = () => publicApi.get("tismain/alumevents/").then((r) => r.data);
export const fetchAlumniStats = () => publicApi.get("tismain/alumni-stats/").then((r) => r.data);
